
import { DISABLE_RUBBER_BAND, RESTORE_PANELS_FOR_NO_MOVEMENT, TERMINATE_ADVANCE_ROOF_SELECTING, SET_EXPOSURE_RUBBER_BAND, SET_DRAW_RUBBER_BAND } from 'actions';
import * as React from 'react';
import { connect } from 'react-redux';
import { Button } from '__common/components/Button';
import {
  MOVE_ARRAY_MODE_DISABLE,
  MOVE_MAP_MODE_DISABLE,
  TOGGLE_EXPOSURE_ENABLE,
  TOGGLE_EXPOSURE_DISABLE,
  DISPLAY_MANUAL_EXPOSURE_ENABLE,
  DISPLAY_MANUAL_EXPOSURE_DISABLE,
  SET_MANUAL_EXPOSURE_KIND,
} from '../../background/backgroundActions';
import { CLEAR_ALL_MANUAL_EXPOSURE } from '../../panels/panelsActions';
import { dispatch as storeDispatch, state as storeState } from '__common/store';
import { launchRecheckPanelExposure } from '../../panels/utils/exposureRecheck';
import { autoSaveProject } from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/saveProject';
import { getProductName } from '__common/constants/products';

type ExposureControlsProps = {
  dispatch: Function;
  toggleExposure: boolean;
  displayManualExposure: boolean;
  moveArrayEnabled: boolean;
  manualExposureKind: 'expose' | 'unexpose' | null;
};

type ExposureControlsState = {
  menuOpen: boolean;
};

const INACTIVE_COLOR = '#666';

const DisplayIcon = ({ active }: { active: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="6" width="9" height="12" rx="1" stroke={active ? '#eb4034' : INACTIVE_COLOR} strokeWidth="2.5" fill={active ? 'rgba(235, 64, 52, 0.15)' : 'none'}/>
    <rect x="13" y="6" width="9" height="12" rx="1" stroke={active ? '#16b6fa' : INACTIVE_COLOR} strokeWidth="1" fill={active ? 'rgba(22, 182, 250, 0.10)' : 'none'}/>
  </svg>
);

class ExposureControls extends React.Component<ExposureControlsProps, ExposureControlsState> {
  state: ExposureControlsState = { menuOpen: this.props.displayManualExposure };

  private wrapperRef: HTMLSpanElement | null = null;

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    if (this.props.displayManualExposure && !this.state.menuOpen) {
      this.setState({ menuOpen: true });
    }
  }

  componentDidUpdate(prevProps: ExposureControlsProps) {
    if (this.props.displayManualExposure && !prevProps.displayManualExposure && !this.state.menuOpen) {
      this.setState({ menuOpen: true });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (e: MouseEvent) => {
    if (this.wrapperRef && !this.wrapperRef.contains(e.target as Node) && this.state.menuOpen) {
      const { displayManualExposure } = this.props;

      if (displayManualExposure) {
        return;
      }
      this.setState({ menuOpen: false });
    }
  }

  resetOtherModes = () => {
    const { dispatch, moveArrayEnabled } = this.props;
    dispatch(DISABLE_RUBBER_BAND());
    dispatch(MOVE_MAP_MODE_DISABLE());
    if (moveArrayEnabled) {
      dispatch(RESTORE_PANELS_FOR_NO_MOVEMENT());
      dispatch(MOVE_ARRAY_MODE_DISABLE());
    }
    dispatch(TERMINATE_ADVANCE_ROOF_SELECTING());
  }

  onIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const { dispatch, displayManualExposure, toggleExposure } = this.props;
    if (displayManualExposure) {
      if (toggleExposure) {
        dispatch(TOGGLE_EXPOSURE_DISABLE());
      }
      dispatch(SET_MANUAL_EXPOSURE_KIND(null));
      dispatch(SET_DRAW_RUBBER_BAND());
      dispatch(DISPLAY_MANUAL_EXPOSURE_DISABLE());
      this.setState({ menuOpen: false });
    } else {
      this.resetOtherModes();
      if (toggleExposure) {
        dispatch(TOGGLE_EXPOSURE_DISABLE());
      }
      dispatch(DISPLAY_MANUAL_EXPOSURE_ENABLE());
      this.setState({ menuOpen: true });
    }
  }

  onSelectKind = (kind: 'expose' | 'unexpose') => {
    const { dispatch, displayManualExposure, toggleExposure, manualExposureKind } = this.props;
    this.resetOtherModes();
    if (toggleExposure && manualExposureKind === kind) {
      dispatch(SET_MANUAL_EXPOSURE_KIND(null));
      dispatch(DISABLE_RUBBER_BAND());
      dispatch(TOGGLE_EXPOSURE_DISABLE());
      this.setState({ menuOpen: true });
      return;
    }
    if (!displayManualExposure) {
      dispatch(DISPLAY_MANUAL_EXPOSURE_ENABLE());
    }
    if (!toggleExposure) {
      dispatch(TOGGLE_EXPOSURE_ENABLE());
    }
    dispatch(SET_MANUAL_EXPOSURE_KIND(kind));
    dispatch(SET_EXPOSURE_RUBBER_BAND());
  }

  onClearAll = () => {
    const { dispatch } = this.props;
    const { background: { selectedRoofId }, projectConfiguration: { productId, projectId } } = storeState();

    dispatch(SET_MANUAL_EXPOSURE_KIND(null));
    dispatch(SET_DRAW_RUBBER_BAND());
    dispatch(TOGGLE_EXPOSURE_DISABLE());
    dispatch(DISPLAY_MANUAL_EXPOSURE_DISABLE());
    this.setState({ menuOpen: false });

    storeDispatch(CLEAR_ALL_MANUAL_EXPOSURE());

    const { panels: { panels } } = storeState();
    const triggerSave = () => {
      autoSaveProject(getProductName(productId), projectId, { allowInPanelsEditor: true });
    };
    if (selectedRoofId !== undefined && panels && panels.length) {
      launchRecheckPanelExposure(panels.map(p => p.id), selectedRoofId, triggerSave, true);
    } else {
      triggerSave();
    }
  }

  renderMenuItem = (
    label: string,
    onClick: () => void,
    opts: { active?: boolean; danger?: boolean } = {},
  ) => {
    const { active, danger } = opts;
    const baseColor = danger ? '#d62828' : active ? '#0083c1' : '#333';
    const baseBg = active ? 'rgba(0, 131, 193, 0.08)' : 'transparent';
    const hoverBg = danger ? 'rgba(214, 40, 40, 0.08)' : 'rgba(0, 131, 193, 0.12)';

    return (
      <div
        onClick={onClick}
        style={{
          padding: '8px 13px',
          cursor: 'pointer',
          fontSize: 13,
          color: baseColor,
          fontWeight: active ? 600 : 400,
          background: baseBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          lineHeight: 1.3,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
        onMouseLeave={(e) => (e.currentTarget.style.background = baseBg)}
      >
        <span>{active ? '\u2713 ' : ''}{label}</span>
      </div>
    );
  }

  render() {
    const { displayManualExposure, toggleExposure, manualExposureKind } = this.props;
    const { menuOpen } = this.state;

    return (
      <span
        className="exposure-controls"
        ref={(el) => (this.wrapperRef = el)}
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      >
        <Button
          variant="text"
          onClick={this.onIconClick}
          className="roofs-selector-button"
          tooltipLabel="Manual Exposure"
        >
          <DisplayIcon active={displayManualExposure} />
        </Button>

        {menuOpen && displayManualExposure && (
          <div
            className="exposure-controls-menu"
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: 'max-content',
              minWidth: 0,
              padding: '2px 0',
              marginTop: 4,
            }}
          >
            {this.renderMenuItem(
              'Add Exposed (E)',
              () => this.onSelectKind('expose'),
              { active: toggleExposure && manualExposureKind === 'expose' },
            )}
            {this.renderMenuItem(
              'Add Non-Exposed (NE)',
              () => this.onSelectKind('unexpose'),
              { active: toggleExposure && manualExposureKind === 'unexpose' },
            )}
            {this.renderMenuItem(
              'Clear All Manual Exposure',
              this.onClearAll,
              { danger: true },
            )}
          </div>
        )}
      </span>
    );
  }
}

function mapStateToProps(state: appState) {
  return {
    toggleExposure: state.background.toggleExposure,
    displayManualExposure: state.background.displayManualExposure,
    moveArrayEnabled: state.background.moveArrayMode,
    manualExposureKind: state.background.manualExposureKind,
  };
}

export default connect(mapStateToProps)(ExposureControls);
